import { toast } from "@/components/common/Toast"
import { Typography } from "@/components/common/Typography/Typography"
import { type DocumentTagEntry } from "@/components/feature/KnowledgebaseFilters/DocumentTagCategoryMenu"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Separator } from "@/components/ui/separator"
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useGetKnowledgeBaseCategories } from "@/hooks/useKnowledgeBaseCategories/useKnowledgeBaseCategories"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useGetKnowledgebaseFiles } from "@/services/api/knowledgebaseService"
import {
  useDeleteQuestionnaireFilterConfig,
  type HydratedQuestionnaireCategoryFilter,
  type QuestionnaireCategoryFilter,
} from "@/services/api/questionnaireService"
import type { KnowledgebaseFile } from "@/types/knowledgebase"
import type { KnowledgeBaseTag } from "@/types/knowledgebaseCategories"
import { classNames } from "@/utils/classNames"
import { Loader } from "@aws-amplify/ui-react"
import { IconSearch, IconX } from "@tabler/icons-react"
import { useEffect, useMemo, useState } from "react"
import { SelectableDocumentRow } from "./SelectableDocumentRow"

const COMPANY_PROFILE_LS_KEY = "selectDocuments:useCompanyProfile"

export const readCompanyProfilePreference = (): boolean => {
  try {
    const stored = localStorage.getItem(COMPANY_PROFILE_LS_KEY)
    return stored === null ? true : stored !== "false"
  } catch {
    return true
  }
}

const writeCompanyProfilePreference = (value: boolean): void => {
  try {
    localStorage.setItem(COMPANY_PROFILE_LS_KEY, String(value))
  } catch {
    // ignore
  }
}

const hasSelectableTagMatch = (
  file: KnowledgebaseFile,
  tagId: string,
): boolean => {
  const matchingTags = file.tags?.filter((tag) => tag.id === tagId) ?? []

  if (matchingTags.length === 0) {
    return file.tagIds?.includes(tagId) ?? false
  }

  return matchingTags.some(
    (tag) => tag.source === "user" || tag.approvedAt !== null, // to filter out unapproved auto tags
  )
}

const createReadonlyKnowledgeBaseTag = (
  categoryId: string,
  tag: HydratedQuestionnaireCategoryFilter["tags"][number],
): KnowledgeBaseTag => ({
  id: tag.id,
  categoryId,
  organizationId: "",
  slug: tag.id,
  displayNameEn: tag.displayNameEn,
  displayNameDe: tag.displayNameDe ?? null,
  sortOrder: 0,
  isActive: true,
  source: "user",
  createdAt: new Date(0),
  updatedAt: new Date(0),
})

const normalizeKnowledgeBaseTag = (
  categoryId: string,
  tag: KnowledgeBaseTag | HydratedQuestionnaireCategoryFilter["tags"][number],
  readOnly: boolean,
): KnowledgeBaseTag =>
  readOnly
    ? createReadonlyKnowledgeBaseTag(
        categoryId,
        tag as HydratedQuestionnaireCategoryFilter["tags"][number],
      )
    : (tag as KnowledgeBaseTag)

export interface SelectDocumentsDialogSelection {
  selectedFiles: KnowledgebaseFile[]
  selectedTagIds: string[]
  categoryFilters: QuestionnaireCategoryFilter[]
  includeUnclassified: boolean
  excludeExpired: boolean
  useCompanyProfile: boolean
}

interface SelectDocumentsDialogProps {
  questionnaireId?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  initialSelectedTagIds?: string[]
  initialHideExpired?: boolean
  readOnly?: boolean
  readOnlyCategoryFilters?: HydratedQuestionnaireCategoryFilter[]
  onConfirm?: (
    selection: SelectDocumentsDialogSelection,
  ) => void | Promise<void>
  onClearSelection?: () => void | Promise<void>
}

export const SelectDocumentsDialog: React.FC<SelectDocumentsDialogProps> = ({
  questionnaireId,
  open,
  onOpenChange,
  initialSelectedTagIds = [],
  initialHideExpired = true,
  readOnly = false,
  readOnlyCategoryFilters = [],
  onConfirm,
  onClearSelection,
}) => {
  const { translate, currentLanguage } = useTranslation()
  const companyProfileEnabled = useFeatureFlag("COMPANY_PROFILE")
  const [search, setSearch] = useState("")
  const [hideExpired, setHideExpired] = useState(initialHideExpired)
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(
    new Set(initialSelectedTagIds),
  )
  const [useCompanyProfile, setUseCompanyProfile] = useState(
    readCompanyProfilePreference,
  )
  const [isConfirming, setIsConfirming] = useState(false)
  const [isClearing, setIsClearing] = useState(false)
  const deleteQuestionnaireFilterConfig = useDeleteQuestionnaireFilterConfig()

  const { data: files = [], isPending: isFilesPending } =
    useGetKnowledgebaseFiles({
      include: ["tags"],
      pollWhileProcessing: false,
    })
  const { data: categories = [], isPending: isCategoriesPending } =
    useGetKnowledgeBaseCategories(false, open && !readOnly)

  const showCategoriesLoading = isCategoriesPending && !readOnly

  const availableCategories = useMemo(
    () =>
      readOnly
        ? readOnlyCategoryFilters.map((categoryFilter) => ({
            id: categoryFilter.categoryId,
            displayNameEn: categoryFilter.categoryDisplayNameEn,
            displayNameDe:
              categoryFilter.categoryDisplayNameDe ??
              categoryFilter.categoryDisplayNameEn,
            color: categoryFilter.categoryColor,
            tags: categoryFilter.tags,
          }))
        : categories.map((category) => ({
            id: category.id,
            displayNameEn: category.displayNameEn,
            displayNameDe: category.displayNameDe ?? category.displayNameEn,
            color: category.color,
            tags: category.tags,
          })),
    [categories, readOnly, readOnlyCategoryFilters],
  )

  const tagById = useMemo(() => {
    const map = new Map<
      string,
      KnowledgeBaseTag & {
        category: {
          id: string
          displayNameEn: string
          displayNameDe: string | null
          color: string | null
        }
      }
    >()
    for (const category of availableCategories) {
      for (const tag of category.tags) {
        const normalizedTag = normalizeKnowledgeBaseTag(
          category.id,
          tag,
          readOnly,
        )

        map.set(tag.id, {
          ...normalizedTag,
          displayNameDe: normalizedTag.displayNameDe ?? null,
          category: {
            id: category.id,
            displayNameEn: category.displayNameEn,
            displayNameDe: category.displayNameDe ?? null,
            color: category.color ?? null,
          },
        })
      }
    }
    return map
  }, [availableCategories, readOnly])

  const baseFilteredFiles = useMemo(() => {
    const now = new Date()

    return files.filter((file) => {
      const isExpired = file.expiryDate
        ? new Date(file.expiryDate) < now
        : false
      if (hideExpired && isExpired) return false
      return true
    })
  }, [files, hideExpired])

  const presentTagIds = useMemo(() => {
    const ids = new Set<string>()
    for (const file of baseFilteredFiles) {
      for (const id of file.tagIds ?? []) {
        if (hasSelectableTagMatch(file, id)) {
          ids.add(id)
        }
      }
    }
    return ids
  }, [baseFilteredFiles])

  const visibleCategories = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase()

    return availableCategories
      .map((cat) => {
        const visibleTags = cat.tags
          .filter((tag) =>
            readOnly ? selectedTagIds.has(tag.id) : presentTagIds.has(tag.id),
          )
          .map((tag) => ({
            ...tag,
            count: baseFilteredFiles.filter((file) =>
              hasSelectableTagMatch(file, tag.id),
            ).length,
          }))

        if (!normalizedSearch) {
          return { ...cat, tags: visibleTags }
        }

        const matchesCategory = [cat.displayNameEn, cat.displayNameDe]
          .filter(Boolean)
          .some((value) =>
            value?.toLocaleLowerCase().includes(normalizedSearch),
          )

        return {
          ...cat,
          tags: visibleTags.filter((tag) => {
            if (matchesCategory) return true

            return [tag.displayNameEn, tag.displayNameDe]
              .filter(Boolean)
              .some((value) =>
                value?.toLocaleLowerCase().includes(normalizedSearch),
              )
          }),
        }
      })
      .filter((cat) => cat.tags.length > 0)
  }, [
    availableCategories,
    baseFilteredFiles,
    presentTagIds,
    readOnly,
    search,
    selectedTagIds,
  ])

  const filteredFiles = useMemo(() => {
    if (selectedTagIds.size === 0) {
      return []
    }

    return baseFilteredFiles.filter((file) => {
      return Array.from(selectedTagIds).some((tagId) =>
        hasSelectableTagMatch(file, tagId),
      )
    })
  }, [baseFilteredFiles, selectedTagIds])

  const showExpiryDateColumn = useMemo(
    () => filteredFiles.some((file) => Boolean(file.expiryDate)),
    [filteredFiles],
  )

  const fileTagEntriesByFileId = useMemo(() => {
    const next = new Map<string, DocumentTagEntry[]>()

    for (const file of filteredFiles) {
      const selectableTagIds = (file.tagIds ?? []).filter((id) =>
        hasSelectableTagMatch(file, id),
      )

      const entries = selectableTagIds
        .map((id) => {
          const tag = tagById.get(id)
          const fileTag =
            file.tags?.find(
              (candidate) =>
                candidate.id === id &&
                (candidate.source === "user" || candidate.approvedAt !== null),
            ) ?? null

          if (!tag) {
            return null
          }

          return { tag, fileTag }
        })
        .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
        .map(({ tag, fileTag }) => ({
          category: {
            categoryId: tag.category.id,
            categoryDisplayNameEn: tag.category.displayNameEn,
            categoryColor: tag.category.color ?? "#6b7280",
            tags: [],
          },
          tag: {
            id: tag.id,
            tagId: tag.id,
            tag,
            source: fileTag?.source ?? "user",
            approvedAt: fileTag?.approvedAt ?? new Date().toISOString(),
            categoryColor: tag.category.color ?? "#6b7280",
          },
        }))

      next.set(file.id, entries)
    }

    return next
  }, [filteredFiles, tagById])

  const toggleTag = (tagId: string) => {
    if (readOnly) {
      return
    }

    setSelectedTagIds((prev) => {
      const next = new Set(prev)
      if (next.has(tagId)) {
        next.delete(tagId)
      } else {
        next.add(tagId)
      }
      return next
    })
  }

  useEffect(() => {
    if (!open) {
      return
    }

    setSearch("")
    setHideExpired(initialHideExpired)
    setSelectedTagIds(new Set(initialSelectedTagIds))
  }, [initialHideExpired, initialSelectedTagIds, open])

  const handleConfirm = async () => {
    const categoryFilters = Array.from(selectedTagIds).reduce<
      QuestionnaireCategoryFilter[]
    >((accumulator, tagId) => {
      const tag = tagById.get(tagId)
      if (!tag) {
        return accumulator
      }

      const existingCategory = accumulator.find(
        (entry) => entry.categoryId === tag.category.id,
      )

      if (existingCategory) {
        existingCategory.tagIds.push(tagId)
        return accumulator
      }

      accumulator.push({
        categoryId: tag.category.id,
        tagIds: [tagId],
      })
      return accumulator
    }, [])

    const selection: SelectDocumentsDialogSelection = {
      selectedFiles: filteredFiles,
      selectedTagIds: Array.from(selectedTagIds),
      categoryFilters,
      includeUnclassified: false,
      excludeExpired: hideExpired,
      useCompanyProfile: companyProfileEnabled ? useCompanyProfile : true,
    }

    if (!onConfirm) {
      onOpenChange(false)
      return
    }

    try {
      setIsConfirming(true)
      await onConfirm(selection)
      onOpenChange(false)
    } finally {
      setIsConfirming(false)
    }
  }

  const handleClear = async () => {
    try {
      setIsClearing(true)
      await deleteQuestionnaireFilterConfig.mutateAsync({ questionnaireId })
      await onClearSelection?.()
      setSearch("")
      setHideExpired(true)
      setSelectedTagIds(new Set())
      onOpenChange(false)
    } catch {
      toast.error(translate("ERROR/SOMETHING_WENT_WRONG"))
    } finally {
      setIsClearing(false)
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (isConfirming || isClearing) {
      return
    }

    if (!open) {
      setSearch("")
      setHideExpired(initialHideExpired)
      setSelectedTagIds(new Set(initialSelectedTagIds))
    }
    onOpenChange(open)
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        className="flex h-full w-full flex-col gap-0 bg-card p-0 sm:max-w-[780px]"
      >
        <SheetHeader className="border-b px-4 py-3">
          <SheetTitle>
            {translate(
              readOnly
                ? "SELECT_DOCUMENTS/READONLY_TITLE"
                : "SELECT_DOCUMENTS/TITLE",
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          <aside className="flex w-full shrink-0 flex-col border-b lg:w-72 lg:border-b-0 lg:border-r">
            <div className="px-5 pb-3 pt-5">
              <Typography
                variant="textXS"
                className="font-medium uppercase tracking-[0.16em] text-muted-foreground"
              >
                {translate("SELECT_DOCUMENTS/FILTER_BY_TAGS")}
              </Typography>
            </div>
            <div className="px-5 pb-4">
              <div className="relative min-w-0">
                {!readOnly ? (
                  <>
                    <IconSearch
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    />
                    <Input
                      placeholder={translate(
                        "SELECT_DOCUMENTS/SEARCH_PLACEHOLDER",
                      )}
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      className="pl-9 pr-9"
                    />
                    {search ? (
                      <button
                        type="button"
                        onClick={() => setSearch("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        aria-label={translate("SELECT_DOCUMENTS/CLEAR_SEARCH")}
                      >
                        <IconX size={14} />
                      </button>
                    ) : null}
                  </>
                ) : null}
              </div>
            </div>
            <ScrollArea className="flex-1">
              <div className="space-y-5 px-5 pb-5">
                {showCategoriesLoading ? (
                  <Typography
                    variant="textXS"
                    className="text-muted-foreground"
                  >
                    {translate("SELECT_DOCUMENTS/LOADING_FILTERS")}
                  </Typography>
                ) : visibleCategories.length === 0 ? (
                  <Typography
                    variant="textXS"
                    className="text-muted-foreground"
                  >
                    {translate("SELECT_DOCUMENTS/NO_TAGS")}
                  </Typography>
                ) : (
                  visibleCategories.map((cat) => (
                    <section key={cat.id} className="space-y-2">
                      <Typography
                        variant="textXS"
                        className="font-medium uppercase tracking-[0.12em] text-muted-foreground"
                      >
                        {currentLanguage === "EN"
                          ? cat.displayNameEn
                          : (cat.displayNameDe ?? cat.displayNameEn)}
                      </Typography>
                      <div className="space-y-2">
                        {cat.tags.map((tag) => (
                          <label
                            key={tag.id}
                            className={classNames(
                              "flex items-center justify-between gap-3 rounded-md px-2 py-1.5",
                              readOnly
                                ? "cursor-default"
                                : "cursor-pointer hover:bg-muted/60",
                            )}
                          >
                            <div className="flex min-w-0 items-center gap-2">
                              {!readOnly ? (
                                <Checkbox
                                  checked={selectedTagIds.has(tag.id)}
                                  onCheckedChange={() => toggleTag(tag.id)}
                                  aria-label={
                                    currentLanguage === "EN"
                                      ? tag.displayNameEn
                                      : (tag.displayNameDe ?? tag.displayNameEn)
                                  }
                                />
                              ) : null}
                              <Typography variant="textXS" className="truncate">
                                {currentLanguage === "EN"
                                  ? tag.displayNameEn
                                  : (tag.displayNameDe ?? tag.displayNameEn)}
                              </Typography>
                            </div>
                            <Typography
                              variant="textXS"
                              className="shrink-0 text-muted-foreground"
                            >
                              {tag.count}
                            </Typography>
                          </label>
                        ))}
                      </div>
                    </section>
                  ))
                )}
              </div>
            </ScrollArea>
          </aside>

          <section className="flex min-h-0 min-w-0 flex-1 flex-col">
            <div className="flex items-center justify-between border-b px-5 py-3">
              <Typography variant="textS">
                {translate("SELECT_DOCUMENTS/RESULT_COUNT", {
                  count: filteredFiles.length,
                })}
              </Typography>
            </div>

            <ScrollArea className="flex-1">
              <div className="px-5 py-4">
                {isFilesPending ? (
                  <Typography
                    variant="textS"
                    className="py-8 text-center text-muted-foreground"
                  >
                    {translate("SELECT_DOCUMENTS/LOADING_DOCUMENTS")}
                  </Typography>
                ) : filteredFiles.length === 0 ? (
                  <Typography
                    variant="textS"
                    className="py-8 text-center text-muted-foreground"
                  >
                    {translate("SELECT_DOCUMENTS/NO_DOCUMENTS")}
                  </Typography>
                ) : (
                  <div
                    role="table"
                    aria-label={translate("SELECT_DOCUMENTS/LIST_ARIA_LABEL")}
                    className="overflow-hidden rounded"
                  >
                    <div
                      role="row"
                      className="flex items-stretch border-b border-grey3 bg-muted/30"
                    >
                      <div
                        role="columnheader"
                        className={classNames(
                          "flex min-w-0 flex-1 items-center px-0 py-2",
                          showExpiryDateColumn ? "border-r border-grey3" : "",
                        )}
                      >
                        <Typography
                          as="span"
                          variant="textXS"
                          className="px-3 font-medium uppercase tracking-[0.12em] text-muted-foreground"
                        >
                          {translate("SELECT_DOCUMENTS/COLUMN_DOCUMENT")}
                        </Typography>
                      </div>
                      {showExpiryDateColumn ? (
                        <div
                          role="columnheader"
                          className="flex w-32 shrink-0 items-center px-3 py-2"
                        >
                          <Typography
                            as="span"
                            variant="textXS"
                            className="font-medium uppercase tracking-[0.12em] text-muted-foreground"
                          >
                            {translate("SELECT_DOCUMENTS/COLUMN_EXPIRY_DATE")}
                          </Typography>
                        </div>
                      ) : null}
                    </div>
                    {filteredFiles.map((file, index) => (
                      <SelectableDocumentRow
                        key={file.id}
                        file={file}
                        tagEntries={fileTagEntriesByFileId.get(file.id) ?? []}
                        isLast={index === filteredFiles.length - 1}
                        showExpiryDateColumn={showExpiryDateColumn}
                      />
                    ))}
                  </div>
                )}
              </div>
            </ScrollArea>
          </section>
        </div>

        {!readOnly ? (
          <>
            <Separator />

            <SheetFooter className="border-t px-6 py-4 sm:flex-row sm:items-center sm:justify-between sm:space-x-0">
              <div className="flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2">
                  <Checkbox
                    checked={hideExpired}
                    onCheckedChange={(checked) =>
                      setHideExpired(Boolean(checked))
                    }
                    aria-label={translate("SELECT_DOCUMENTS/HIDE_EXPIRED")}
                    disabled={isConfirming || isClearing}
                  />
                  <Typography variant="textS">
                    {translate("SELECT_DOCUMENTS/HIDE_EXPIRED")}
                  </Typography>
                </label>
                {companyProfileEnabled ? (
                  <label className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2">
                    <Checkbox
                      checked={useCompanyProfile}
                      onCheckedChange={(checked) => {
                        const next = Boolean(checked)
                        setUseCompanyProfile(next)
                        writeCompanyProfilePreference(next)
                      }}
                      aria-label={translate(
                        "SELECT_DOCUMENTS/USE_COMPANY_PROFILE",
                      )}
                      disabled={isConfirming || isClearing}
                    />
                    <Typography variant="textS">
                      {translate("SELECT_DOCUMENTS/USE_COMPANY_PROFILE")}
                    </Typography>
                  </label>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClear}
                  disabled={isConfirming || isClearing || !questionnaireId}
                >
                  {isClearing ? <Loader filledColor="black" /> : null}
                  {translate("SELECT_DOCUMENTS/CLEAR_SELECTION")}
                </Button>
                <Button
                  onClick={handleConfirm}
                  disabled={
                    isConfirming || isClearing || filteredFiles.length <= 0
                  }
                  className={classNames(
                    "min-w-56",
                    filteredFiles.length === 0
                      ? "bg-yellow1 text-black hover:bg-yellow1/90"
                      : "",
                  )}
                >
                  {isConfirming ? <Loader filledColor="black" /> : null}
                  {translate("SELECT_DOCUMENTS/CONFIRM", {
                    count: filteredFiles.length,
                  })}
                </Button>
              </div>
            </SheetFooter>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}
